<template>
    <div>
        <AppBreadCrumb :paths="[{ title: $t('adminUser.adminUsers') }]" :select="true">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.search')"
                class="search-input"
                @search="onSearch"
            />
            <a-button v-b-modal.user-form class="actionButtonBreadcrumb" @click="showModalWithoutEdit">
                <md-icon>add</md-icon>
                {{ $t("adminUser.newAdminUser") }}
            </a-button>
            <a-button v-b-modal.user-form class="actionButtonBreadcrumb ml-1" @click="startPaymentWorker">
                <span class="material-icons" style="font-size: 12px; margin-right: 5px"> system_update_alt </span>
                Start Payment Worker
            </a-button>
            <a-button v-b-modal.user-form class="actionButtonBreadcrumb ml-1" @click="startInvoiceWorker">
                <span class="material-icons" style="font-size: 12px; margin-right: 5px"> system_update_alt </span>
                Start Invoice Worker
            </a-button>
        </AppBreadCrumb>

        <b-container fluid>
            <b-row>
                <b-col>
                    <div class="table-scroll-wrapper">
                        <a-table
                            :key="'vtable-' + renderCount"
                            :columns="cols()"
                            :dataSource="getFilteredRows()"
                            :loading="isBusy"
                            :locale="{ emptyText: $t('message.noNewAdminUser') }"
                            :pagination="false"
                            :scroll="{ x: 'max-content' }"
                            class="custom-table2"
                            rowKey="_id"
                            @change="tableChange"
                        >
                        <div :key="'statusSlot' + $i18n.locale" :slot="'statusSlot'" slot-scope="data, item">
                            <md-switch
                                v-model="item.enable"
                                class="md-primary"
                                style="margin: 0"
                                @change="(e) => toggleChange(item)(e)"
                            >
                                <span v-if="item.enable">{{ $t("message.active") }}</span>
                                <span v-else>{{ $t("message.passive") }}</span>
                            </md-switch>
                        </div>
                        <div :key="'actionsSlot-' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                            <div class="d-flex w-100 justify-content-center">
                                <a-button class="actionButton d-flex align-items-center" @click="edit(item._id)">
                                    <span style="margin-right: 16px; display: block">{{ $t("message.edit") }}</span>
                                    <ActionEdit />
                                </a-button>
                                <a-button
                                    v-if="loggedUser._id !== item._id"
                                    class="actionButton d-flex align-items-center"
                                    @click="remove(item._id)"
                                >
                                    <span style="margin-right: 16px; display: block">{{
                                        $t("message.deleteField")
                                    }}</span>
                                    <ActionDelete />
                                </a-button>
                            </div>
                        </div>
                    </a-table>
                    </div>
                    <div class="d-flex justify-content-end">
                        <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="adminUsers.total" />
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <Modal2
            v-if="isModalShow"
            id="user-form"
            :title="editMode ? $t('adminUser.updateAdminUserForm') : $t('adminUser.newAdminUserForm')"
            :show-footer="false"
            @cancel="hideModal"
        >
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                <b-row>
                    <b-col md="6">
                        <md-field
                            :class="{
                                'md-invalid': errors.first($t('message.name')) != null,
                            }"
                        >
                            <label>{{ $t("message.name") }}</label>
                            <md-input
                                v-model="adminUser.name"
                                v-validate="'required'"
                                :name="$t('message.name')"
                            ></md-input>
                            <span class="md-error">{{ errors.first($t("message.name")) }}</span>
                        </md-field>
                    </b-col>
                    <b-col md="6">
                        <md-field
                            :class="{
                                'md-invalid': errors.first($t('message.email')) != null,
                            }"
                        >
                            <label>{{ $t("message.email") }}</label>
                            <md-input
                                v-model="adminUser.email"
                                v-validate="'required|email'"
                                :name="$t('message.email')"
                                autocomplete="off"
                                trim
                            ></md-input>
                            <span class="md-error">{{ errors.first($t("message.email")) }}</span>
                        </md-field>
                    </b-col>
                    <b-col md="6">
                        <md-field
                            :class="{
                                'md-invalid': errors.first($t('message.password')) != null,
                            }"
                        >
                            <label>{{ $t("message.passwordAtLeast", { num: "6" }) }}</label>
                            <md-input
                                :ref="$t('message.password')"
                                v-model="adminUser.password"
                                v-validate="editMode ? 'min:6' : 'required|min:6'"
                                :name="$t('message.password')"
                                autocomplete="off"
                                trim
                                type="password"
                            ></md-input>
                            <span class="md-error">{{ errors.first($t("message.password")) }}</span>
                        </md-field>
                    </b-col>
                    <b-col md="6">
                        <md-field
                            :class="{
                                'md-invalid': errors.first($t('message.passwordRepeat')) != null,
                            }"
                        >
                            <label>{{ $t("message.passwordRepeat") }}</label>
                            <md-input
                                v-model="passwordConfirmation"
                                v-validate="
                                    editMode
                                        ? 'confirmed:' + $t('message.password') + '|min:6'
                                        : 'required|confirmed:' + $t('message.password') + '|min:6'
                                "
                                :name="$t('message.passwordRepeat')"
                                autocomplete="off"
                                type="password"
                            ></md-input>
                            <span class="md-error">{{ errors.first($t("message.passwordRepeat")) }}</span>
                        </md-field>
                    </b-col>
                    <b-col md="12">
                        <md-switch v-model="adminUser.enable" class="md-primary">
                            <span v-if="adminUser.enable">{{ $t("message.active") }}</span>
                            <span v-else>{{ $t("message.passive") }}</span>
                        </md-switch>
                        <md-switch v-model="adminUser.isFullAccess" class="md-primary">
                            <span>Full Access</span>
                        </md-switch>
                    </b-col>
                    <b-col md="12" v-if="!adminUser.isFullAccess">
                        <div style="display: flex; gap: 15px; flex-wrap: wrap">
                            <div v-for="page of defaultModel.approvedPages" :key="page.page">
                                <b-form-group :label="page.displayName || page.page">
                                    <b-form-checkbox
                                        :checked="['read', 'write'].includes(getPageRole(page.page))"
                                        name="menu"
                                        @change="() => toggleCheckState(page.page, 'read')"
                                    >
                                        Okuma
                                    </b-form-checkbox>
                                    <b-form-checkbox
                                        :checked="['write'].includes(getPageRole(page.page))"
                                        name="menu"
                                        @change="() => toggleCheckState(page.page, 'write')"
                                    >
                                        Yazma
                                    </b-form-checkbox>
                                </b-form-group>
                            </div>
                        </div>
                    </b-col>
                    <b-col md="12">
                        <b-button :disabled="loading" type="submit" variant="primary">
                            <template v-if="!loading">
                                <span v-if="!editMode">{{ $t("message.save") }}</span>
                                <span v-else>{{ $t("message.update") }}</span>
                            </template>
                            <template v-else>
                                <font-awesome-icon icon="spinner" spin />
                            </template>
                        </b-button>
                        <b-button @click="hideModal">
                            <template>
                                {{ $t("message.cancel") }}
                            </template>
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../components/common/AppBreadCrumb.vue";
import { log2 } from "../../utils/log2";
import * as _ from "lodash";
import { tableSorter } from "../../utils/utils";
import Pagination from "../../components/Pagination.vue";
import ActionEdit from "../../components/svg/ActionEdit.vue";
import ActionDelete from "../../components/svg/ActionDelete.vue";
import Modal2 from "../../components/common/Modal2.vue";
import { startInvoiceWorker, startPaymentWorker } from "../../api/billing";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);

const defaultModel = {
    _id: null,
    username: "",
    email: "",
    password: "",
    enable: true,
    isFullAccess: true,
    approvedPages: [
        { displayName: "Cluster Dashboard", page: "cluster-dashboard", role: "write" },
        { displayName: "Duyurular", page: "announcements", role: "write" },
        { displayName: "Kuponlar", page: "coupons", role: "write" },
        { displayName: "Invoices", page: "invoices", role: "write" },
        { displayName: "Admin Users", page: "admin-users", role: "write" },
        { displayName: "Sites", page: "sites", role: "write" },
        { displayName: "Global Settings", page: "global-settings", role: "write" },
        { displayName: "Plan Management", page: "plan-management", role: "write" },
        { displayName: "Error Reports", page: "error-reports", role: "write" },
        { displayName: "Admin Raporlar", page: "admin-reports", role: "write" },
        { displayName: "Chat", page: "customer-chat", role: "write" },
        { displayName: "Chatbot", page: "customer-chatbot", role: "write" },
        { displayName: "Visitors", page: "customer-visitors", role: "write" },
        { displayName: "Mail", page: "customer-mails", role: "write" },
        { displayName: "Task", page: "customer-tasks", role: "write" },
        { displayName: "Ticket", page: "customer-ticket", role: "write" },
        { displayName: "Archive", page: "customer-archive", role: "write" },
        { displayName: "Users", page: "customer-users", role: "write" },
        { displayName: "Hazır Içerik", page: "customer-content", role: "write" },
        { displayName: "Tetikleyiciler", page: "customer-trigger", role: "write" },
        { displayName: "Ayarlar", page: "customer-settings", role: "write" },
        { displayName: "Integrations", page: "customer-integrations", role: "write" },
        { displayName: "Billing", page: "customer-billing", role: "write" },
        { displayName: "Raporlar", page: "customer-report", role: "write" },
        { displayName: "Otomasyon ", page: "automations", role: "write" },
        { displayName: "Tablolar ", page: "tables", role: "write" },
        { displayName: "Form ", page: "customer-forms", role: "write" },
    ],
};
const log = log2("AdminUsers");
export default {
    name: "AdminUsers",
    components: { AppBreadCrumb, Pagination, ActionEdit, ActionDelete, Modal2 },
    data() {
        return {
            isModalShow: false,
            isBusy: true,
            loading: false,
            editMode: false,
            pageSizes: ["10", "25", "50", "100"],
            searchText: undefined,
            adminUser: _.cloneDeep(defaultModel),
            defaultModel,
            passwordConfirmation: "",
            cols: () => [
                {
                    dataIndex: "name",
                    title: this.$t("adminUser.adminUserName"),
                    ...tableSorter("name", "string", this.currentSort),
                },
                {
                    dataIndex: "email",
                    title: this.$t("message.email"),
                    ...tableSorter("email", "string", this.currentSort),
                },
                {
                    dataIndex: "enable",
                    title: this.$t("message.status"),
                    scopedSlots: { customRender: "statusSlot" },
                    ...tableSorter("enable", "string", this.currentSort),
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                },
            ],
            perPage: 100,
            page: 1,
            renderCount: 0,
            currentSort: {
                columnKey: "enable",
                order: "ascend",
            },
        };
    },
    computed: {
        ...mapState({
            adminUsers: (state) => state.adminUser.adminUsers,
            loggedUser: (state) => state.auth.adminUser,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        ...mapActions({
            getAdminUser: "adminUser/getAdminUser",
            getAdminUsers: "adminUser/getAdminUsers",
            createAdminUser: "adminUser/createAdminUser",
            updateAdminUser: "adminUser/updateAdminUser",
            deleteAdminUser: "adminUser/deleteAdminUser",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.isModalShow = true;
            this.editMode = false;
            this.adminUser = _.cloneDeep(defaultModel);
        },
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            
            // Sayfalama parametrelerini güncelle
            if (pagination) {
                if (pagination.current !== undefined) {
                    this.page = pagination.current;
                }
                if (pagination.pageSize !== undefined) {
                    this.perPage = pagination.pageSize;
                }
            }
            
            // Sıralama parametrelerini güncelle
            if (sorter && sorter.columnKey) {
                const newSort = {
                    columnKey: sorter.columnKey,
                    order: sorter.order,
                };
                const oldSort = this.currentSort;
                
                if (!_.isEqual(newSort, oldSort)) {
                    this.currentSort = newSort;
                    this.renderCount += 1;
                }
            }
            
            // Verileri yeniden yükle
            this.get();
        },
        getFilteredRows() {
            return this.searchText
                ? this.adminUsers.data.filter(
                      (x) =>
                          x.name.toLowerCase().includes(this.searchText) ||
                          x.email.toLowerCase().includes(this.searchText)
                  )
                : this.adminUsers.data;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
            // Sayfa değiştiğinde verileri yeniden yükle
            this.get();
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.page = current;
            this.perPage = pageSize;
        },
        get() {
            this.isBusy = true;

            this.getAdminUsers({ perPage: this.perPage, page: this.page }).then(() => {
                this.isBusy = false;
            });
        },
        edit(id) {
            this.getAdminUser(id).then((response) => {
                this.editMode = true;
                this.adminUser = _.cloneDeep(response);
                this.adminUser.password = "";

                /*this.$bvModal.show("user-form");*/
                this.showModal();
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.douYouWishToDeleteUser"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteAdminUser(id);
                    this.$toasted.show(this.$t("message.userDeleted"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                });
        },
        reset() {
            this.adminUser = {
                ...defaultModel,
            };
        },
        toggleChange(adminUser) {
            return (newState) => {
                adminUser.enable = newState;
                this.updateAdminUser(adminUser);
            };
        },
        handleSave() {
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.loading = true;

                    if (this.adminUser.isFullAccess) {
                        this.adminUser.approvedPages = this.adminUser.approvedPages.map((page) => ({
                            ...page,
                            role: "write",
                        }));
                    }

                    const result = this.adminUser._id
                        ? this.updateAdminUser(this.adminUser)
                        : this.createAdminUser(this.adminUser);

                    result
                        .then(() => {
                            this.hideModal();
                            this.$toasted.show(this.$t("message.userSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            this.reset();
                            this.get();
                        })
                        .finally(() => (this.loading = false));
                }
            });
        },
        startPaymentWorker() {
            startPaymentWorker().then(() => {
                this.$toasted.show("Payment Worker has been started.", {
                    duration: 3000,
                    position: "top-center",
                });
            });
        },
        startInvoiceWorker() {
            startInvoiceWorker().then(() => {
                this.$toasted.show("Invoice Worker has been started.", {
                    duration: 3000,
                    position: "top-center",
                });
            });
        },

        getPage(page) {
            return this.adminUser.approvedPages.find((x) => x.page === page);
        },
        getPageRole(page) {
            return this.adminUser.approvedPages.find((x) => x.page === page)?.role;
        },
        toggleCheckState(pageId, role) {
            console.log("toggleCheckState", pageId, role);
            const foundPageIndex = this.adminUser.approvedPages.findIndex((x) => x.page === pageId);
            const foundPage = this.adminUser.approvedPages[foundPageIndex];
            if (foundPage) {
                const oldRole = foundPage.role;
                const newRole = role;
                if (newRole === "write") {
                    if (oldRole === "write") {
                        this.adminUser.approvedPages[foundPageIndex] = {
                            page: pageId,
                            role: "read",
                        };
                    } else {
                        this.adminUser.approvedPages[foundPageIndex] = {
                            page: pageId,
                            role: "write",
                        };
                    }
                } else {
                    this.adminUser.approvedPages = this.adminUser.approvedPages.filter((x) => x.page !== pageId);
                }
            } else {
                this.adminUser.approvedPages.push({ page: pageId, role: role });
            }

            this.adminUser = _.cloneDeep(this.adminUser);
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
    },
    mounted() {
        this.get();
    },
    beforeMount() {
        const isAccessEnabled =
            this.loggedUser.isFullAccess || this.loggedUser.approvedPages.some((page) => page.page === "admin-users");
        if (!isAccessEnabled) {
            this.$router.push("/denied");
        }
    },
    watch: {
        "adminUser.isFullAccess": function (newVal) {
            if (newVal) {
                this.adminUser.approvedPages = this.defaultModel.approvedPages.map((page) => ({
                    ...page,
                    role: "write",
                }));
            }
        },
        // page ve perPage watch'ları kaldırıldı
        // Çünkü pageChange ve tableChange fonksiyonları zaten get() çağırıyor
        // Bu sayede çift çağrı önleniyor
    },
};
</script>

<style lang="scss" scoped>
#user-form {
    button {
        float: right;
        margin-left: 10px;
    }
}

/**
 * Tablo için yatay scroll wrapper
 * Küçük ekranlarda tablonun sağa sola kaydırılabilmesini sağlar
 */
.table-scroll-wrapper {
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch; /* iOS için smooth scroll */
    
    /* Scrollbar stilleri (opsiyonel - daha iyi görünüm için) */
    &::-webkit-scrollbar {
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: var(--color-border-default, #f0f0f0);
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        
        &:hover {
            background: #a8a8a8;
        }
    }
}
</style>
